import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { startWith, takeUntil } from 'rxjs';
import { PwnType } from 'src/app/modules/pwn/shared/enums/pwn-type';
import { IReferenceData } from '../../interfaces/form/reference-data.model';
import { AbstractComponent } from '../abstract-component';
import { Reason } from '../../interfaces/reason';
import { contactDescriptions } from '../../constants/constants';
import { StudentProfile } from '../../interfaces/student-profile';
import { StudentService } from 'src/app/services/student.service';
import { ParentsInfo } from '../../interfaces/student';

@Component({
  selector: 'app-mail-to',
  templateUrl: './mail-to.component.html',
  styleUrls: ['./mail-to.component.scss'],
  standalone: false,
})
export class MailToComponent extends AbstractComponent implements OnInit {
  @Input() referenceData: Record<string, IReferenceData[]>;
  @Input() mailToForm: FormGroup;
  @Input() studentDetails: StudentProfile;
  @Input() pwnType: PwnType;
  @Input() disableFillDetails: boolean;

  outreachType: typeof PwnType = PwnType;
  contactList: Reason[] = [];
  eligibleParents: ParentsInfo[] = [];
  recipientContactdata: Reason | number;

  readonly primaryParentIndex: number = 0;
  readonly firstContactIndex: number = 0;
  readonly eligibleParentsIndex: number = 2;

  constructor(private studentService: StudentService) {
    super();
  }

  ngOnInit(): void {
    // Find Primary Contact
    const primaryContact: ParentsInfo = this.studentDetails?.parentDetails.find(
      (parent: ParentsInfo) => {
        return parent.isPrimary;
      }
    );

    //If the primary contact exists, then add it to the contact list and populate the eligible parents array
    if (primaryContact) {
      this.contactList.push({
        id: 0,
        description: 'Primary',
      });
      this.eligibleParents.push(primaryContact);
    }

    if (!this.disableFillDetails) {
      const defaultValue: number = primaryContact
        ? this.contactList[0].id
        : null;
      this.mailToForm.get('recipientContact')?.setValue(defaultValue);
      this.fillContactDetails(defaultValue);
    }

    this.mailToForm
      .get('recipientContact')
      ?.valueChanges.subscribe((selectedValue: Reason) => {
        this.fillContactDetails(selectedValue.id);
      });

    if (
      !this.mailToForm.get('postalMail') &&
      !this.mailToForm.get('parentPortal')
    ) {
      this.mailToForm.controls.parentPortal.setValidators([
        Validators.required,
      ]);
      this.mailToForm.controls.postalMail.setValidators([Validators.required]);
      this.mailToForm.controls.parentPortal.setValue(null);
      this.mailToForm.controls.postalMail.setValue(null);
    }
    this.initValueChanges();
    this.defaultRecipientContact();
  }

  private defaultRecipientContact() {
    if (!this.mailToForm.get('recipientContact')?.value) {
      this.mailToForm
        .get('recipientContact')
        ?.setValue(this.contactList[this.firstContactIndex], {
          emitEvent: false,
        });
    }
  }

  private initValueChanges() {
    this.mailToForm.valueChanges
      .pipe(
        takeUntil(this.componentDestroyed),
        startWith(this.mailToForm?.value)
      )
      .subscribe({
        next: () => {
          if (
            this.mailToForm.value.parentPortal === true ||
            this.mailToForm.value.postalMail === true
          ) {
            this.mailToForm.controls.parentPortal.removeValidators([
              Validators.required,
            ]);
            this.mailToForm.controls.postalMail.removeValidators([
              Validators.required,
            ]);
          } else if (
            this.mailToForm.value.parentPortal === false &&
            this.mailToForm.value.postalMail === false
          ) {
            // setting null, since unchecking checkbox sets the control value to false.
            //False is a valid value form hence it is not throwing an error to user. So manually setting null to let form to validate.
            this.mailToForm.controls.parentPortal.setValue(null);
            this.mailToForm.controls.postalMail.setValue(null);
          } else {
            this.mailToForm.controls.parentPortal.setValidators([
              Validators.required,
            ]);
            this.mailToForm.controls.postalMail.setValidators([
              Validators.required,
            ]);
          }
          this.mailToForm.controls.postalMail.updateValueAndValidity({
            emitEvent: false,
          });
          this.mailToForm.controls.parentPortal.updateValueAndValidity({
            emitEvent: false,
          });
        },
      });
  }

  fillContactDetails(index: number): void {
    if (this.eligibleParents?.length > index) {
      if (this.pwnType === this.outreachType.RequestAdditionalAssessments) {
        this.recipientContactdata = {
          id: index,
          description:
            index === this.primaryParentIndex
              ? contactDescriptions.primary
              : contactDescriptions.secondary,
        };
      } else if (this.pwnType === this.outreachType.RequestForReEval) {
        this.recipientContactdata = {
          id: index,
          description:
            index === this.primaryParentIndex
              ? contactDescriptions.primary
              : contactDescriptions.secondary,
        };
      } else if (this.pwnType === this.outreachType.AdditionalReEval) {
        this.recipientContactdata = {
          id: index,
          description:
            index === this.primaryParentIndex
              ? contactDescriptions.primary
              : contactDescriptions.secondary,
        };
      } else if (this.pwnType === this.outreachType.RefusalReEval) {
        this.recipientContactdata = {
          id: index,
          description:
            index === this.primaryParentIndex
              ? contactDescriptions.primary
              : contactDescriptions.secondary,
        };
      } else if (this.pwnType === this.outreachType.NoticeOfIR) {
        this.recipientContactdata = {
          id: index,
          description:
            index === this.primaryParentIndex
              ? contactDescriptions.primary
              : contactDescriptions.secondary,
        };
      } else if (this.pwnType === this.outreachType.Initial) {
        this.recipientContactdata = {
          id: index,
          description:
            index === this.primaryParentIndex
              ? contactDescriptions.primary
              : contactDescriptions.secondary,
        };
      } else if (this.pwnType === this.outreachType.PreSchool) {
        this.recipientContactdata = {
          id: index,
          description:
            index === this.primaryParentIndex
              ? contactDescriptions.primary
              : contactDescriptions.secondary,
        };
      } else if (this.pwnType === this.outreachType.ReevaluationAnnual) {
        this.recipientContactdata = {
          id: index,
          description:
            index === this.primaryParentIndex
              ? contactDescriptions.primary
              : contactDescriptions.secondary,
        };
      } else {
        this.recipientContactdata = index;
      }

      this.mailToForm.patchValue(
        {
          recipientContact: this.recipientContactdata,
          recipientName:
            this.eligibleParents[index]?.firstName +
            ' ' +
            this.eligibleParents[index]?.lastName,
          recipientEmail: this.eligibleParents[index]?.emailAddress,
          recipientAddress: (
            this.eligibleParents[index]?.address?.streetNumber ?? ''
          ).trim(),
          recipientAddress2: (
            this.eligibleParents[index]?.address?.streetName ?? ''
          ).trim(),
          recipientApartmentNumber: (
            this.eligibleParents[index]?.address?.apartmentNo ?? ''
          ).trim(),
          recipientCity: (
            this.eligibleParents[index]?.address?.city ?? ''
          ).trim(),
          recipientState: this.eligibleParents[index]?.address?.state ?? null,
          recipientZip: this.eligibleParents[index]?.address?.zipCode ?? '',
        },
        { emitEvent: false }
      );
    } else {
      this.mailToForm.patchValue(
        {
          recipientName: '',
          recipientEmail: '',
          recipientAddress: '',
          recipientAddress2: '',
          recipientApartmentNumber: '',
          recipientCity: '',
          recipientState: null,
          recipientZip: '',
        },
        { emitEvent: false }
      );
    }
  }

  override afterOnDestroy(): void {
    // afterOnDestroy
  }
}
